<template>
    <div>
        <div v-if="template_context === 'custom_automated_message'">
            <strong>When will this message be sent</strong>

            <ul>
                <li>When triggered by a RESTful api call to <code>{{ custom_webhook_url }}</code></li>
            </ul>
        </div>

        <div v-if="template_context === 'auto-message-provide-tracking-number'">
            <strong>When will this message be sent</strong>

            <ul>
                <li>RedChirp will send this message whenever it observes a shipment in your CRM (e.g. Commerce7, etc.) <strong>with a tracking number</strong></li>
                <li>RedChirp will only send this message if it becomes aware of the shipment on the day it was made or the calendar day prior -- if you enter shipments more than two days after they are made, automated messages <strong>will not go out</strong></li>
                <li>The message will be sent once per <strong>shipment</strong> -- if an order has more than one shipment then this message will be sent multiple times for that <strong>order</strong> (once per <strong>shipment</strong>)</li>
                <li>The message will be sent to all phone numbers that appear on the order</li>
            </ul>
        </div>

        <div v-if="template_context === 'auto-message-credit-card-decline'">
            <strong>When will this message be sent</strong>

            <ul>
                <li>RedChirp will send this message whenever a new tender fails because a card was declined</li>
                <li>Failure must be observed by RedChirp within 48 hours or no message will be sent</li>
            </ul>
        </div>

        <div v-if="template_context === 'list-drip'">
            <strong>When will this message be sent</strong>

            <ul>
                <li v-if="list_drip_delay != null && list_drip_delay < 60000">
                    RedChirp will send this message shortly after a member is added to the list <code>{{ list_drip_list_internal_label }}</code>
                </li>

                <li v-else>
                    RedChirp will send this message {{ prettyDuration(list_drip_delay) }} after a member is added to the list <code>{{ list_drip_list_internal_label }}</code>
                </li>

                <li>
                    The send time is <em>approximate</em>.  Messages may be delayed up to one hour.
                </li>
            </ul>
        </div>

        <div v-if="commerce_7_email_when_will_it_send != null">
            <strong>When will this message be sent</strong>

            <ul>
                <li>In Commerce7, you can view and configure Transaction Emails from Settings (left navigation) > Transaction Emails</li>
                <li>For details on when Commerce7 triggers a particular transactional email, you can consult the <a href="https://documentation.commerce7.com/setting-up-transactional-emails#trigger" target="_blank">the Commerce7 Transaction email types &amp; triggers documentation.</a></li>
                <li>This message is sent when Commerce7 sends the transaction email "<strong>{{ commerce_7_email_when_will_it_send.label }}</strong>" </li>
                <li v-if="commerce_7_email_when_will_it_send.fourteen_day_limit">The message will not be sent to the same customer more than once in a <strong>14 day period.</strong></li>
                <li>The message will be sent as soon as Commerce7 notifies us the email has been sent.</li>
                <li>If it is outside of the Send During Hours you have set, RedChirp will hold the message for a maximum of one day and send as soon as it is during the Send During Hours</li>
            </ul>
        </div>

        <div v-if="template_context.includes('wine-shipping')">
            <strong>Notes on this automated message</strong>

            <ul>
                <li v-if="template_context === 'wine-shipping-info-received'">RedChirp will send this message whenever Wineshipping reports that a package's information has been received (label created)</li>
                <li v-if="template_context === 'wine-shipping-info-received'">If your organization creates labels far in advance of shipping, consider using the Wineshipping: First In Transit Event Observed message instead</li>

                <li v-if="template_context === 'wine-shipping-first-in-transit'">RedChirp will send this message whenever Wineshipping reports that the first in-transit event for the package</li>
                <li v-if="template_context === 'wine-shipping-first-in-transit'">Use this message when your organization is in the habit of creating labels far in advance of shipping the item</li>

                <li v-if="template_context === 'wine-shipping-delivered'">RedChirp will send this message whenever Wineshipping reports that a package has been delivered</li>
                <li v-if="template_context === 'wine-shipping-available-for-pickup'">RedChirp will send this message whenever Wineshipping reports that a package is ready for pickup</li>
                <li v-if="template_context === 'wine-shipping-out-for-delivery'">RedChirp will send this message whenever Wineshipping reports that a package is out for delivery</li>
                <li v-if="template_context === 'wine-shipping-delivery-attempt-failed'">RedChirp will send this message whenever Wineshipping reports that a delivery attempt failed</li>
                <li v-if="template_context === 'wine-shipping-exception'">RedChirp will send this message whenever Wineshipping reports that an exception (e.g. weather delay, package returned, etc.) has occoured.</li>
                <li>The message will go to each number listed in the Wineshipping's <code>smses</code> field</li>
                <li>The customer name comes from Wineshipping's one <code>customer_name</code> field</li>
            </ul>
        </div>

        <div v-if="template_context.includes('wine-co-')">
            <strong>Notes on this automated message</strong>

            <ul>
                <li v-if="template_context === 'wine-co-shipped'">RedChirp will send this message whenever Wine Co reports that a package's information has been received (label created)</li>
                <li v-if="template_context === 'wine-co-delivered'">RedChirp will send this message whenever Wine Co reports that a package has been delivered</li>
                <li v-if="template_context === 'wine-co-available-for-pickup'">RedChirp will send this message whenever Wine Co reports that a package is ready for pickup</li>
                <li v-if="template_context === 'wine-co-out-for-delivery'">RedChirp will send this message whenever Wine Co reports that a package is out for delivery</li>
                <li v-if="template_context === 'wine-co-delivery-attempt-fail'">RedChirp will send this message whenever Wine Co reports that a delivery attempt failed</li>
                <li v-if="template_context === 'wine-co-picked-up'">RedChirp will send this message whenever Wine Co reports that a package has been picked up</li>

                <li>The message will go to each number listed in Wine Co's <code>home_phone</code>, <code>work_phone</code> and/or <code>mobile_phone</code> fields</li>
                <li>The customer name comes from Wineshipping's one <code>recipient_first_name</code> and <code>recipient_last_name</code> field</li>
            </ul>
        </div>

        <div v-if="template_context.includes('ship-engine')">
            <strong>Notes on this automated message</strong>

            <ul>
                <li><strong>Fee Notice:</strong> When you have the ShipEngine API turned on, there is a cost of $0.02 (two cents) per package shipped -- this cost is per package, not per message sent.  Therefore, if one package triggered several automated messages (e.g. out for delivery, delivered) you would only be charged one fee of once cent.  These fees only apply to packages that are shipped while the integration is enabled.  You can disable this integration at any time.</li>
                <li v-if="template_context === 'ship-engine-shipped'">RedChirp will send this message whenever ShipEngine reports that a package's information has been received (label created)</li>
                <li v-if="template_context === 'ship-engine-delivered'">RedChirp will send this message whenever ShipEngine reports that a package has been delivered</li>
                <li v-if="template_context === 'ship-engine-available-for-pickup'">RedChirp will send this message whenever ShipEngine reports that a package is ready for pickup</li>
                <li v-if="template_context === 'ship-engine-out-for-delivery'">RedChirp will send this message whenever ShipEngine reports that a package is out for delivery</li>
                <li v-if="template_context === 'ship-engine-delivery-attempt-fail'">RedChirp will send this message whenever ShipEngine reports that a delivery attempt failed</li>
                <li v-if="template_context === 'ship-engineo-picked-up'">RedChirp will send this message whenever ShipEngine reports that a package has been picked up</li>
                <li>The message will go to each number listed in your paired (e.g. Commerce7, eCellar, OrderPort, WineDirect, etc.) POS order data.  <strong>Messages will be sent to both bill to and ship to contacts on the order.</strong></li>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OtherMessageInfo',

    props: {
        template_context: {
            type: String,
            required: true
        },
        custom_webhook_url: {
            type: String,
            default: ''
        },
        list_drip_delay: {
            type: Number,
            default: null
        },
        list_drip_list_internal_label: {
            type: String,
            default: ''
        },
        commerce_7_email_when_will_it_send: {
            type: Object,
            default: null
        }
    },

    methods: {
        prettyDuration(milliseconds) {
            if (!this.$rc || !this.$rc.pretty) return '';
            return this.$rc.pretty(milliseconds, 'duration');
        }
    }
}
</script>
